import { useState } from 'react';
import Modal, { Field } from '../../../components/Modal.jsx';
import { get, post } from '../../../lib/api.js';
import { useAsync } from '../../../lib/hooks.js';
import { toast } from '../../../lib/toast.jsx';
import { errorMessage } from '../../../lib/errors.js';
import { formatMonthly } from '../../../lib/format.js';
import { domainErrorText, validateDomain } from '../../../lib/domain.js';
import { useT } from '../../../lib/i18n.jsx';

export default function NewPlanModal({ lang, onClose, onCreated }) {
  const t = useT();

  const { data: packages, loading, error: pkgError } = useAsync(() => get('/packages'), []);

  const [domain, setDomain] = useState('');
  const [touched, setTouched] = useState(false);
  const [packageId, setPackageId] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const parsed = validateDomain(domain);
  const domainError = touched && !parsed.ok ? domainErrorText(parsed.code, lang) : '';
  const activePackages = (packages || []).filter((p) => p.is_active);
  const selected = activePackages.find((p) => String(p.id) === String(packageId));

  const submit = async (e) => {
    e.preventDefault();
    setTouched(true);
    if (!parsed.ok || !packageId) return;
    setErr('');
    setBusy(true);
    try {
      const hosting = await post('/hostings', {
        domain: parsed.domain,
        package_id: Number(packageId),
      });
      toast(t('newPlan.created'));
      onCreated?.(hosting);
    } catch (e2) {
      setErr(errorMessage(e2, { lang }));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      as="form"
      onSubmit={submit}
      onClose={onClose}
      maxWidth={520}
      title={t('newPlan.title')}
      description={t('newPlan.description')}
      footer={
        <>
          <button type="button" className="btn-ghost" onClick={onClose}>
            {t('g.cancel')}
          </button>
          <button type="submit" className="btn-primary" disabled={busy || loading}>
            {busy ? t('detailUi.working') : t('newPlan.create')}
          </button>
        </>
      }
    >
      <div className="modal-body">
        {err && <div className="login-error" role="alert">{err}</div>}
        {pkgError && (
          <div className="login-error" role="alert">
            {t('newPlan.packages.error', { error: errorMessage(pkgError, { lang }) })}
          </div>
        )}
        <div className="login-form">
          <Field label={t('dom.col.domain')} error={domainError}>
            <input
              required
              placeholder={t('newPlan.domain.placeholder')}
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              maxLength={253}
              value={domain}
              onChange={(e) => setDomain(e.target.value)}
              onBlur={() => domain && setTouched(true)}
            />
          </Field>
          <Field
            label={t('newPlan.package')}
            hint={selected?.description || undefined}
          >
            <select
              required
              value={packageId}
              onChange={(e) => setPackageId(e.target.value)}
              disabled={loading || !!pkgError}
            >
              <option value="">
                {loading ? t('newPlan.packages.loading') : t('newPlan.package.choose')}
              </option>
              {activePackages.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name} — {formatMonthly(p.price_monthly, lang)}
                </option>
              ))}
            </select>
          </Field>
        </div>
      </div>
    </Modal>
  );
}
